/**
 * Copyright 2013 Google, Inc.
 * Copyright 2015 Daishinsha Inc.
 * Copyright 2019 Vivliostyle Foundation
 *
 * Vivliostyle.js is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Vivliostyle.js is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Vivliostyle.js.  If not, see <http://www.gnu.org/licenses/>.
 *
 * @fileoverview CssStyler - Apply CSS cascade to a document incrementally and
 * cache the result.
 */
import * as Base from "./base";
import * as Break from "./break";
import * as CmykStore from "./cmyk-store";
import * as CounterStyle from "./counter-style";
import * as Css from "./css";
import * as CssCascade from "./css-cascade";
import * as CssParser from "./css-parser";
import * as CssProp from "./css-prop";
import * as CssValidator from "./css-validator";
import * as Display from "./display";
import * as Exprs from "./exprs";
import * as LayoutProcessor from "./layout-processor";
import * as Vtree from "./vtree";
import { CssStyler, XmlDoc } from "./types";

export class SlipRange {
  endStuckFixed: number;
  endFixed: number;
  endSlipped: number;

  constructor(endStuckFixed, endFixed, endSlipped) {
    this.endStuckFixed = endStuckFixed;
    this.endFixed = endFixed;
    this.endSlipped = endSlipped;
  }
}

/**
 * Maps all ints in a range ("fixed") to ints with slippage ("slipped")
 */
export class SlipMap {
  map = [] as SlipRange[];

  getMaxSlipped(): number {
    if (this.map.length == 0) {
      return 0;
    }
    const range = this.map.at(-1);
    return range.endSlipped;
  }

  addStuckRange(endFixed: number): void {
    if (this.map.length == 0) {
      this.map.push(new SlipRange(endFixed, endFixed, endFixed));
    } else {
      const range = this.map.at(-1);
      const endSlipped = range.endSlipped + endFixed - range.endFixed;
      if (range.endFixed == range.endStuckFixed) {
        range.endFixed = endFixed;
        range.endStuckFixed = endFixed;
        range.endSlipped = endSlipped;
      } else {
        this.map.push(new SlipRange(endFixed, endFixed, endSlipped));
      }
    }
  }

  addSlippedRange(endFixed: number): void {
    if (this.map.length == 0) {
      this.map.push(new SlipRange(endFixed, 0, 0));
    } else {
      this.map.at(-1).endFixed = endFixed;
    }
  }

  slippedByFixed(fixed: number): number {
    const index = Base.binarySearch(
      this.map.length,
      (index) => fixed <= this.map[index].endFixed,
    );
    const range = this.map[index];
    return range.endSlipped - Math.max(0, range.endStuckFixed - fixed);
  }

  /**
   * Smallest fixed for a given slipped.
   */
  fixedBySlipped(slipped: number): number {
    const index = Base.binarySearch(
      this.map.length,
      (index) => slipped <= this.map[index].endSlipped,
    );
    const range = this.map[index];
    return range.endStuckFixed - (range.endSlipped - slipped);
  }
}

type CounterSnapshot = {
  offset: number;
  counters: CssCascade.CounterValues;
  changes: string[];
  changeTypes?: { [key: string]: "reset" | "set" | "increment" };
};

function cloneCounterValues(
  counters: CssCascade.CounterValues,
): CssCascade.CounterValues {
  const result = {} as CssCascade.CounterValues;
  Object.keys(counters).forEach((name) => {
    result[name] = Array.from(counters[name]);
  });
  return result;
}

export interface FlowListener {
  encounteredFlowChunk(flowChunk: Vtree.FlowChunk, flow: Vtree.Flow): void;
}

export interface AbstractStyler extends CssStyler.AbstractStyler {}

/**
 * Represent a box generated by a (pseudo)element. When constructed, a box
 * corresponding to `::before` pseudoelement is also constructed and stored in
 * `beforeBox` property, whereas one corresponding `::after` pseudoelement is
 * not constructed and `afterBox` property is `null`. `afterBox` is constructed
 * by `buildAfterPseudoElementBox` method.
 * @param style Cascaded style values for the box.
 * @param offset The start offset of the box. It coincides with the start offset
 *     of the element if the box is generated by the element or the `::before`
 *     pseudoelement. When the box corresponds to the `::after` pseudoelement,
 *     the offset is just after the content before the `::after` pseudoelement.
 * @param isRoot True if the box is generated by the root element (not
 *     pseudoelement).
 * @param flowChunk FlowChunk to which the box belongs to.
 * @param atBlockStart True if the box is right after the block start edge.
 * @param atFlowStart True if the box is right after the flow start.
 * @param isParentBoxDisplayed True if the parent box has a displayed box.
 */
export class Box {
  flowName: string;
  isBlockValue: boolean | null = null;
  hasBoxValue: boolean | null = null;
  styleValues = {} as { [key: string]: Css.Val | null };
  beforeBox: Box | null = null;
  afterBox: Box | null = null;
  breakBefore: string | null = null;

  constructor(
    public readonly context: Exprs.Context,
    public readonly style: CssCascade.ElementStyle,
    public readonly offset: number,
    public readonly isRoot: boolean,
    public readonly flowChunk: Vtree.FlowChunk,
    public readonly atBlockStart: boolean,
    public readonly atFlowStart: boolean,
    public readonly isParentBoxDisplayed: boolean,
  ) {
    this.flowName = flowChunk.flowName;
    if (this.hasBox()) {
      const pseudoMap = style["_pseudos"];
      if (pseudoMap) {
        if (pseudoMap["before"]) {
          const beforeBox = new Box(
            context,
            pseudoMap["before"],
            offset,
            false,
            flowChunk,
            this.isBlock(),
            atFlowStart,
            true,
          );
          const beforeContent = beforeBox.styleValue("content");
          if (Vtree.nonTrivialContent(beforeContent)) {
            this.beforeBox = beforeBox;
            this.breakBefore = beforeBox.breakBefore;
          }
        }
      }
    }
    this.breakBefore = Break.resolveEffectiveBreakValue(
      this.getBreakValue("before"),
      this.breakBefore,
    );
    if (this.atFlowStart && Break.isForcedBreakValue(this.breakBefore)) {
      flowChunk.breakBefore = Break.resolveEffectiveBreakValue(
        flowChunk.breakBefore,
        this.breakBefore,
      );
    }
  }

  /**
   * Build a box corresponding to `::after` pseudoelement and stores it in
   * `afterBox` property.
   * @param offset The start offset of the `::after` pseudoelement box, which is
   *     just after the content before the `::after` pseudoelement.
   * @param atBlockStart True if the box is right after the block start edge.
   * @param atFlowStart True if the box is right after the flow start.
   */
  buildAfterPseudoElementBox(
    offset: number,
    atBlockStart: boolean,
    atFlowStart: boolean,
  ) {
    if (this.hasBox()) {
      const pseudoMap = this.style["_pseudos"];
      if (pseudoMap) {
        if (pseudoMap["after"]) {
          const afterBox = new Box(
            this.context,
            pseudoMap["after"],
            offset,
            false,
            this.flowChunk,
            atBlockStart,
            atFlowStart,
            true,
          );
          const afterContent = afterBox.styleValue("content");
          if (Vtree.nonTrivialContent(afterContent)) {
            this.afterBox = afterBox;
          }
        }
      }
    }
  }

  styleValue(name: string, defaultValue?: Css.Val): Css.Val | null {
    if (!(name in this.styleValues)) {
      const cv = this.style[name] as CssCascade.CascadeValue;
      this.styleValues[name] = cv
        ? cv.evaluate(this.context, name)
        : defaultValue || null;
    }
    return this.styleValues[name];
  }

  displayValue(): Css.Val {
    return this.styleValue("display", Css.ident.inline);
  }

  isBlock(): boolean {
    if (this.isBlockValue === null) {
      const display = this.displayValue() as Css.Ident;
      const position = this.styleValue("position") as Css.Ident;
      const float = this.styleValue("float") as Css.Ident;
      this.isBlockValue = Display.isBlock(
        display,
        position,
        float,
        this.isRoot,
      );
    }
    return this.isBlockValue;
  }

  hasBox(): boolean {
    if (this.hasBoxValue === null) {
      this.hasBoxValue =
        this.isParentBoxDisplayed &&
        this.displayValue() !== Css.ident.none &&
        !Display.isRunning(this.styleValue("position"));
    }
    return this.hasBoxValue;
  }

  getBreakValue(edge: string): string | null {
    let breakValue: string | null = null;
    if (this.isBlock()) {
      const val = this.styleValue(`break-${edge}`);
      if (val) {
        breakValue = val.toString();
      }
    }
    return breakValue;
  }
}

/**
 * Manages boxes generated by elements as a stack.
 */
export class BoxStack {
  stack = [] as Box[];
  atBlockStart: boolean = true; // indicates if the next pushed box will be at the block start
  atFlowStart: boolean = true; // indicates if the next pushed box will be at the flow start
  atStartStack = [] as { atBlockStart: boolean; atFlowStart: boolean }[]; // pushed when a new flow starts

  constructor(public readonly context: Exprs.Context) {}

  /**
   * Returns if the stack is empty.
   */
  empty(): boolean {
    return this.stack.length === 0;
  }

  /**
   * Returns the last box in the stack.
   */
  lastBox(): Box | undefined {
    return this.stack.at(-1);
  }

  /**
   * Returns the flow name of the last box in the stack.
   */
  lastFlowName(): string | null {
    const lastBox = this.lastBox();
    return lastBox ? lastBox.flowChunk.flowName : null;
  }

  /**
   * Returns if the last box in the stack is displayed.
   */
  isCurrentBoxDisplayed(): boolean {
    return this.stack.every((box) => box.displayValue() !== Css.ident.none);
  }

  /**
   * Create a new box and push it to the stack.
   * @param style Cascaded style values for the box.
   * @param offset The start offset of the box.
   * @param isRoot True if the box is generated by the root element
   * @param newFlowChunk Specify if the element is a flow element (i.e. the
   *     element is specified a new `flow-into` value)
   */
  push(
    style: CssCascade.ElementStyle,
    offset: number,
    isRoot: boolean,
    newFlowChunk?: Vtree.FlowChunk,
  ): Box {
    const lastBox = this.lastBox();
    if (newFlowChunk && lastBox && newFlowChunk.flowName !== lastBox.flowName) {
      this.atStartStack.push({
        atBlockStart: this.atBlockStart,
        atFlowStart: this.atFlowStart,
      });
    }
    const flowChunk = newFlowChunk || lastBox.flowChunk;
    const isAtFlowStart = this.atFlowStart || !!newFlowChunk;
    const isParentBoxDisplayed = this.isCurrentBoxDisplayed();
    const box = new Box(
      this.context,
      style,
      offset,
      isRoot,
      flowChunk,
      isAtFlowStart || this.atBlockStart,
      isAtFlowStart,
      isParentBoxDisplayed,
    );
    this.stack.push(box);
    this.atBlockStart = box.hasBox()
      ? !box.beforeBox && box.isBlock()
      : this.atBlockStart;
    this.atFlowStart = box.hasBox()
      ? !box.beforeBox && isAtFlowStart
      : this.atFlowStart;
    return box;
  }

  encounteredTextNode(node: Node) {
    const box = this.lastBox();
    if (
      (node.nodeType === Node.TEXT_NODE ||
        node.nodeType === Node.CDATA_SECTION_NODE) &&
      (this.atBlockStart || this.atFlowStart) &&
      box.hasBox()
    ) {
      const whitespaceValue = box
        .styleValue("white-space", Css.ident.normal)
        .toString();
      const whitespace = Vtree.whitespaceFromPropertyValue(whitespaceValue);
      if (whitespace && !Vtree.canIgnore(node, whitespace)) {
        this.atBlockStart = false;
        this.atFlowStart = false;
      }
    }
  }

  /**
   * Pop the last box.
   */
  pop(offset: number): Box {
    const box = this.stack.pop();
    box.buildAfterPseudoElementBox(offset, this.atBlockStart, this.atFlowStart);
    if (this.atFlowStart && box.afterBox) {
      const breakBefore = box.afterBox.getBreakValue("before");
      box.flowChunk.breakBefore = Break.resolveEffectiveBreakValue(
        box.flowChunk.breakBefore,
        breakBefore,
      );
    }
    const parent = this.lastBox();
    if (parent) {
      if (parent.flowName === box.flowName) {
        if (box.hasBox()) {
          this.atBlockStart = this.atFlowStart = false;
        }
      } else {
        const atStart = this.atStartStack.pop();
        this.atBlockStart = atStart.atBlockStart;
        this.atFlowStart = atStart.atFlowStart;
      }
    }
    return box;
  }

  /**
   * Find the start offset of the nearest block start edge to which the
   * `break-before` value of the box should be propagated. This method can be
   * called when after pushing the box into the stack or after popping the box
   * out of the stack.
   */
  nearestBlockStartOffset(box: Box): number {
    if (!box.atBlockStart) {
      return box.offset;
    }
    let i = this.stack.length - 1;
    let parent = this.stack[i];

    // When called just after the box is popped out, the last box in the stack
    // is different from the box and it is the parent of the box. When called
    // after the box is pushed, the last box in the stack is identical to the
    // box and the parent of the box is a box right before the specified box.
    if (parent === box) {
      i--;
      parent = this.stack[i];
    }
    while (i >= 0) {
      if (parent.flowName !== box.flowName) {
        return box.offset;
      }
      if (!parent.atBlockStart) {
        return parent.offset;
      }
      if (parent.isRoot) {
        return parent.offset;
      }
      box = parent;
      parent = this.stack[--i];
    }
    throw new Error("No block start offset found!");
  }
}

export class StyleStore implements CssCascade.StyleReader {
  private readonly map = new Map<number, CssCascade.ElementStyle>();

  constructor(private readonly xmldoc: XmlDoc.XMLDocHolder) {}

  setAt(offset: number, style: CssCascade.ElementStyle): void {
    this.map.set(offset, style);
  }

  styleOf(element: Element): CssCascade.ElementStyle {
    return this.map.get(this.xmldoc.getElementOffset(element));
  }
}

export class Styler implements AbstractStyler {
  root: Base.ChildElement;
  last: Base.RootBoundCursor | null;
  rootStyle = {} as CssCascade.ElementStyle;
  /**
   * writing-mode and direction values propagated from the body element to
   * the root element. Per CSS Writing Modes spec, this propagation is done
   * on used values rather than computed values, so these values must not be
   * inherited by the page context. (Issue #1122)
   */
  bodyPropagatedStyle = {} as CssCascade.ElementStyle;
  styles: StyleStore;
  counterSnapshots: CounterSnapshot[] = [];
  flows = {} as { [key: string]: Vtree.Flow };
  flowChunks = [] as Vtree.FlowChunk[];
  flowListener: FlowListener | null = null;
  flowToReach: string | null = null;
  idToReach: string | null = null;
  cascade: CssCascade.CascadeInstance;
  // the last element the cascade opened, kept across pops: vgen reads it when
  // it re-evaluates generated content
  elementWindow: CssCascade.ElementCascadeInstance;
  offsetMap: SlipMap;
  primary: boolean = true;
  primaryStack = [] as boolean[];
  rootBackgroundAssigned: boolean = false;
  rootLayoutAssigned: boolean = false;
  lastOffset: number;
  breakBeforeValues = new Map<number, string | null>();
  boxStack: BoxStack;
  bodyReached: boolean = true;

  constructor(
    public readonly xmldoc: XmlDoc.XMLDocHolder,
    cascade: CssCascade.Cascade,
    public readonly scope: Exprs.LexicalScope,
    public readonly context: Exprs.Context,
    public readonly primaryFlows: { [key: string]: boolean },
    public readonly validatorSet: CssValidator.ValidatorSet,
    public readonly counterListener: CssCascade.CounterListener,
    counterResolver: CssCascade.CounterResolver,
    counterStyleStore: CounterStyle.CounterStyleStore,
    cmykStore: CmykStore.CmykStore,
    mergeValidatorSet: CssValidator.ValidatorSet | null,
  ) {
    this.root = xmldoc.root;
    this.styles = new StyleStore(xmldoc);
    this.last = Base.RootBoundCursor.atRoot(this.root);
    this.cascade = cascade.createInstance(
      context,
      counterListener,
      counterResolver,
      counterStyleStore,
      cmykStore,
      this.root,
      scope,
      validatorSet,
      this.styles,
      mergeValidatorSet,
    );
    this.offsetMap = new SlipMap();
    const rootOffset = xmldoc.getElementOffset(this.root);
    this.lastOffset = rootOffset;
    this.boxStack = new BoxStack(context);
    this.offsetMap.addStuckRange(rootOffset);
    const style = this.getAttrStyle(this.root);
    this.elementWindow = this.cascade.pushElement(this.root, style, rootOffset);
    this.recordCounterSnapshot(
      rootOffset,
      this.cascade.counters,
      this.cascade.lastCounterChanges,
      this.cascade.lastCounterChangeTypes,
    );
    this.postprocessTopStyle(style, false);
    this.determineRootSizes(style);
    switch (this.root.namespaceURI) {
      case Base.NS.XHTML:
        this.bodyReached = false;
        break;
    }
    this.primaryStack.push(true);
    this.styles.setAt(rootOffset, style);
    this.lastOffset++;
    this.replayFlowElementsFromOffset(-1);
  }

  hasProp(
    style: CssCascade.ElementStyle,
    map: ReadonlyMap<string, Css.Val>,
    name: string,
  ): boolean {
    const cascVal = style[name] as CssCascade.CascadeValue;
    return cascVal && cascVal.evaluate(this.context) !== map.get(name);
  }

  transferPropsToRoot(
    srcStyle: CssCascade.ElementStyle,
    map: ReadonlyMap<string, Css.Val>,
  ): void {
    for (const [pname, val] of map) {
      const cascval = srcStyle[pname];
      if (cascval) {
        this.rootStyle[pname] = cascval;
        delete srcStyle[pname];
      } else if (val) {
        this.rootStyle[pname] = new CssCascade.CascadeValue(
          val,
          CssParser.SPECIFICITY_AUTHOR,
        );
      }
    }
  }

  /**
   * Transfer properties that should be applied on the container (partition)
   * level to this.rootStyle.
   * @param elemStyle (source) element style
   */
  postprocessTopStyle(
    elemStyle: CssCascade.ElementStyle,
    isBody: boolean,
  ): void {
    if (isBody) {
      for (const propName of ["writing-mode", "direction"]) {
        if (elemStyle[propName] && !this.rootStyle[propName]) {
          // Copy it over, but keep it at the body element as well.
          this.rootStyle[propName] = elemStyle[propName];
          // Record the value propagated from the body element so that it
          // can be excluded from the page context inheritance. (Issue #1122)
          this.bodyPropagatedStyle[propName] = elemStyle[propName];
        }
      }
    } else {
      for (const propName in elemStyle) {
        if (CssCascade.isInherited(propName)) {
          this.rootStyle[propName] = elemStyle[propName];
        }
      }
    }
    if (!this.rootBackgroundAssigned) {
      const backgroundColor = this.hasProp(
        elemStyle,
        this.validatorSet.backgroundProps,
        "background-color",
      )
        ? (elemStyle["background-color"] as CssCascade.CascadeValue).evaluate(
            this.context,
          )
        : null;
      const backgroundImage = this.hasProp(
        elemStyle,
        this.validatorSet.backgroundProps,
        "background-image",
      )
        ? (elemStyle["background-image"] as CssCascade.CascadeValue).evaluate(
            this.context,
          )
        : null;
      if (
        (backgroundColor && !Css.isDefaultingValue(backgroundColor)) ||
        (backgroundImage && !Css.isDefaultingValue(backgroundImage))
      ) {
        // css-backgrounds-3 §2.11: the element does not paint this background.
        this.transferPropsToRoot(elemStyle, this.validatorSet.backgroundProps);
        // background-position-x/-y are not part of the `background` shorthand
        // grammar, so they are absent from backgroundProps. Move them only when
        // the source style actually has them: transferPropsToRoot assigns a
        // default for every property of the map, and a default axis value here
        // would override the background-position moved just above.
        for (const pname of [
          "background-position-x",
          "background-position-y",
        ]) {
          const cascval = elemStyle[pname];
          if (cascval) {
            this.rootStyle[pname] = cascval;
            delete elemStyle[pname];
          }
        }
        this.rootBackgroundAssigned = true;
      }
    }
    if (!this.rootLayoutAssigned) {
      for (let i = 0; i < columnProps.length; i++) {
        if (
          this.hasProp(elemStyle, this.validatorSet.layoutProps, columnProps[i])
        ) {
          this.transferPropsToRoot(elemStyle, this.validatorSet.layoutProps);
          this.rootLayoutAssigned = true;
          break;
        }
      }
    }
  }

  private determineRootSizes(elemStyle: CssCascade.ElementStyle): void {
    this.context.rootFontMetricsReady = false;
    this.context.resolvingRootFontSize = true;
    // Root font-size and line-height self-references use initial font metrics,
    // including when root sizes are recomputed after a script loads fonts.
    this.context.rootLineHeight =
      this.context.measureFontUnit("lh", this.context.initialFontSize) ??
      this.context.initialFontSize * Exprs.fallbackLineHeightRatio;
    const fontSize = elemStyle["font-size"] as CssCascade.CascadeValue;
    let isRelativeFontSize = true;
    let isInvalidFontSize = false;
    if (fontSize && !Css.isDefaultingValue(fontSize.value)) {
      const evaluated = fontSize.evaluate(this.context);
      const val = this.resolveRootSizingCalc(evaluated);
      isInvalidFontSize = !CSS.supports("font-size", val.toString());
      const fromRelativeCalc =
        evaluated instanceof Css.Func && val instanceof Css.Numeric;
      // The absolute size keywords (e.g. "small") and the relative keywords
      // ("larger"/"smaller") are relative to the default (initial) font size
      // and must be resolved to a numeric root font size, so that all
      // consumers (rem units, rlh, page context, the root element's own
      // inherited properties) use the same value. (Issue #2174)
      let px: number | null = CssCascade.resolveAbsoluteFontSizeKeyword(
        val,
        this.context.initialFontSize,
      );
      if (
        px == null &&
        (CssCascade.hasKeywordName(val, "larger") ||
          CssCascade.hasKeywordName(val, "smaller"))
      ) {
        px = CssCascade.resolveRelativeFontSizeKeyword(
          val as Css.Ident,
          this.context.initialFontSize,
        );
      }
      if (px == null && val instanceof Css.Numeric) {
        px = val.num;
        switch (val.unit) {
          case "em":
          case "rem":
            px *= this.context.initialFontSize;
            break;
          case "%":
            px *= this.context.initialFontSize / 100;
            break;
          case "lh":
          case "rlh":
            px *= this.context.rootLineHeight;
            break;
          default: {
            const unitSize = Exprs.defaultUnitSizes[val.unit];
            const metric = this.context.measureFontUnit(
              val.unit,
              this.context.initialFontSize,
            );
            if (unitSize) {
              px *= unitSize;
              isRelativeFontSize = fromRelativeCalc;
            } else if (metric != null) {
              px *= metric;
              isRelativeFontSize = true;
            } else if (Exprs.isViewportRelativeLengthUnit(val.unit)) {
              // The unit size of a viewport relative unit, e.g. `2vw`, must not
              // be left as the raw number either, and the root element must use
              // the resolved size like above: the internal units of this engine
              // (`pv*`) are not CSS that a browser can resolve. A plain
              // viewport relative unit is normally already converted to a px
              // length by `fontSize.evaluate()` (the page size is not known
              // while the root sizes are determined, so the viewer viewport
              // provides the unit size), so this branch only applies to a unit
              // that survives the evaluation. (Review)
              px *= this.context.queryUnitSize(val.unit, true);
              isRelativeFontSize = true;
            } else {
              // Any other dimension is not a length that Vivliostyle can
              // resolve here, e.g. the `5s` that a var() substitution put into
              // `font-size: var(--size)`: the root element keeps the initial
              // font size rather than treating the number as pixels, as the
              // browser keeps it for an invalid value. (Review)
              px = null;
            }
          }
        }
      }
      if (px != null && !Number.isFinite(px)) {
        // A conversion that produced a non-finite value must not become the
        // root font size either. (Review)
        px = null;
      }
      if (px == null) {
        // A font size that is neither a keyword nor a Css.Numeric: a unitless
        // zero, or a calc()/clamp()/min()/max() whose arguments are lengths
        // that can be resolved here, e.g.
        // `:root { font-size: clamp(10px, 20px, 30px) }`. The root element has
        // no parent, so `em`/`%` arguments refer to the initial font size.
        // (Issue #2174 follow-up)
        const resolved = CssCascade.resolveFontSizeValueToPx(
          this.context,
          val,
          this.context.initialFontSize,
        );
        if (resolved != null) {
          px = resolved;
          if (val instanceof Css.Func) {
            // As for a calc() that the root sizing could resolve, the root
            // element must use this value. (Issues #608, #549)
            isRelativeFontSize = true;
          } else {
            // A unitless zero, which is an absolute length like `0px`, so the
            // root element uses it directly.
            isRelativeFontSize = false;
          }
        }
      }
      // A font size that is neither a keyword nor resolvable here, e.g. the
      // `round(up, 20px, 7px)` whose rounding strategy this engine does not
      // evaluate, is left to the browser, which keeps the declaration and
      // computes the value: the root font size that this context records stays
      // the initial one, so `rem`, the page context and the margin boxes use a
      // different metric than the rendered root element. Known limitation:
      // materializing the computed value of the browser would need the
      // rendered size. (Review)
      if (px != null) {
        // `font-size` has a non-negative computed-value range, e.g.
        // `calc(10px - 20px)` computes to 0px.
        px = Math.max(0, px);
        this.context.rootFontSize = px;
        this.context.isRelativeRootFontSize = isRelativeFontSize;
      }
    }
    this.context.resolvingRootFontSize = false;
    this.context.rootFontProperties = CssCascade.getFontMetricProperties(
      elemStyle,
      this.context,
    );
    const rootFontWeight = elemStyle["font-weight"] as
      CssCascade.CascadeValue | undefined;
    const rootWeight = rootFontWeight?.evaluate(this.context, "font-weight");
    if (
      rootWeight instanceof Css.Ident &&
      (CssCascade.hasKeywordName(rootWeight, "bolder") ||
        CssCascade.hasKeywordName(rootWeight, "lighter"))
    ) {
      // The root has no parent element, so relative weights use the initial
      // font weight. Resolve the keyword before measuring: the hidden probe is
      // appended to the viewer body, whose weight may differ from the source
      // document's initial weight.
      this.context.rootFontProperties["font-weight"] = String(
        CssCascade.resolveRelativeFontWeight(rootWeight, 400),
      );
    }
    // A default root font-size can stay implicit while root-metric terms in
    // calc() already need to use the source root's font settings.
    this.context.rootFontMetricsReady =
      this.context.rootFontSize != null ||
      isInvalidFontSize ||
      !fontSize ||
      Css.isDefaultingValue(fontSize.value);
    const rootFontSize =
      this.context.rootFontSize ?? this.context.initialFontSize;
    const lineHeight = elemStyle["line-height"] as CssCascade.CascadeValue;
    let rootLineHeight: number | null = null;
    // An unspecified root line-height is the initial value `normal`.
    let normalLineHeight =
      !lineHeight || Css.isDefaultingValue(lineHeight.value);
    let fromRelativeCalc = false;
    if (lineHeight && !Css.isDefaultingValue(lineHeight.value)) {
      const evaluated = lineHeight.evaluate(this.context);
      const val = this.resolveRootSizingCalc(evaluated);
      // A value that the browser rejects, e.g. an invalid var() substitution,
      // leaves the initial `normal`.
      normalLineHeight =
        CssCascade.hasKeywordName(val, "normal") ||
        CssCascade.isInvalidLineHeight(this.context, val);
      fromRelativeCalc =
        evaluated instanceof Css.Func && val instanceof Css.Numeric;
      if (val instanceof Css.Num) {
        // An overflowing calculation, e.g. `line-height: calc(exp(1000))`, is
        // clamped by the browser: an infinite root line height would propagate
        // into every `rlh` unit and into the page context instead. (Review)
        rootLineHeight = Number.isFinite(val.num * rootFontSize)
          ? val.num * rootFontSize
          : null;
      } else if (val instanceof Css.Numeric) {
        let px: number | null = val.num;
        switch (val.unit) {
          case "em":
          case "rem":
            px = val.num * rootFontSize;
            break;
          case "%":
            px = (val.num * rootFontSize) / 100;
            break;
          case "lh":
          case "rlh":
            px = val.num * this.context.rootLineHeight;
            break;
          default: {
            const unitSize = Exprs.defaultUnitSizes[val.unit];
            const metric = this.context.measureFontUnit(
              val.unit,
              rootFontSize,
              this.context.rootFontProperties,
            );
            if (unitSize) {
              px = val.num * unitSize;
            } else if (metric != null) {
              px = val.num * metric;
              fromRelativeCalc = true;
            } else if (Exprs.isViewportRelativeLengthUnit(val.unit)) {
              px = val.num * this.context.queryUnitSize(val.unit, true);
              // The root element must use this resolved size: an internal unit
              // such as `2pvw` is not CSS that a browser can resolve, and the
              // `rlh` unit and the page context use this value. A plain
              // viewport relative unit is normally already converted to a px
              // length by `lineHeight.evaluate()`, because the page size is not
              // known while the root sizes are determined (the viewer viewport
              // provides the unit size), so this branch only applies to a unit
              // that survives the evaluation. (Review)
              fromRelativeCalc = true;
            } else {
              // An unsupported dimension keeps the default root line height,
              // as the browser keeps it for an invalid value. (Review)
              px = null;
            }
          }
        }
        rootLineHeight = px != null && Number.isFinite(px) ? px : null;
      } else if (val instanceof Css.Func) {
        // A math function of CSS Values 4 that is not a `calc()`, e.g.
        // `:root { line-height: round(40px, 7px) }` or `min(40px, 2em)`:
        // `resolveRootSizingCalc()` only reduces a `calc()`, so the value is
        // reduced with the resolver that the inherited line heights use, which
        // also knows those functions. The `em`/`%` arguments of the root
        // element refer to its own font size, and its `lh`/`rlh` to the line
        // height that is being resolved (the root has no parent). (Review)
        const resolved = CssCascade.resolveLineHeightValueToPx(
          this.context,
          val,
          rootFontSize,
          this.context.rootLineHeight,
          this.context.rootFontProperties,
        );
        if (resolved != null) {
          rootLineHeight = resolved;
          // A function of unitless numbers, e.g. the `min(1, 2)` of
          // `:root { line-height: min(1, 2) }`, is a `<number>`: its computed
          // value is a multiplier of the font size, so the root element keeps
          // the declaration, as it does for a plain unitless number. Only the
          // recorded root line height is in px, for `rlh` and the page
          // context. (Review)
          fromRelativeCalc = !CssCascade.isUnitlessNumberValue(val);
        }
        // A value that is not resolvable here is left to the browser, as the
        // root font size above: the root line height that this context records
        // stays the preferred one, and `rlh` and the page context use a
        // different metric than the rendered root element. Known limitation,
        // as above. (Review)
      }
    }
    this.context.rootLineHeight =
      rootLineHeight ??
      (normalLineHeight
        ? this.context.measureFontUnit(
            "lh",
            rootFontSize,
            this.context.rootFontProperties,
          )
        : null) ??
      rootFontSize * Exprs.fallbackLineHeightRatio;
    this.context.isRootLineHeightFromRelativeCalc = fromRelativeCalc;
  }

  private resolveRootSizingCalc(val: Css.Val): Css.Val {
    if (!(val instanceof Css.Func)) {
      return val;
    }
    // filtering visitors always return a value
    return val.visit(
      new CssCascade.RootSizingCalcFilterVisitor(this.context),
    ) as Css.Val;
  }

  getTopContainerStyle(): CssCascade.ElementStyle {
    let offset = 0;
    while (!this.bodyReached) {
      offset += 5000;
      if (this.styleUntil(offset, 0) == Number.POSITIVE_INFINITY) {
        break;
      }
    }
    return this.rootStyle;
  }

  getAttrStyle(elem: Element): CssCascade.ElementStyle {
    // skip cases in which elements for XML other than HTML or SVG
    // have 'style' attribute not for CSS declaration
    if ((elem as any).style instanceof CSSStyleDeclaration) {
      const styleAttrValue = elem.getAttribute("style");
      if (styleAttrValue) {
        return CssCascade.parseStyleAttribute(
          this.scope,
          this.validatorSet,
          this.xmldoc.url,
          styleAttrValue,
        );
      }
    }
    return {} as CssCascade.ElementStyle;
  }

  recordCounterSnapshot(
    offset: number,
    counters: CssCascade.CounterValues,
    changes: string[] = [],
    changeTypes?: { [key: string]: "reset" | "set" | "increment" },
  ): void {
    const snapshot = {
      offset,
      counters: cloneCounterValues(counters),
      changes: Array.from(changes || []),
      changeTypes: changeTypes ? { ...changeTypes } : undefined,
    } as CounterSnapshot;
    const lastSnapshot = this.counterSnapshots.at(-1);
    // If multiple updates occur at the same offset (e.g., relayout),
    // keep the latest snapshot so counters reflect the most recent state.
    if (lastSnapshot && lastSnapshot.offset === offset) {
      this.counterSnapshots[this.counterSnapshots.length - 1] = snapshot;
      return;
    }
    this.counterSnapshots.push(snapshot);
  }

  getCounterSnapshotAtOffset(offset: number): CounterSnapshot | null {
    if (!this.counterSnapshots.length) {
      return null;
    }
    const index = Base.binarySearch(
      this.counterSnapshots.length,
      (i) => offset < this.counterSnapshots[i].offset,
    );
    const targetIndex = index === 0 ? 0 : index - 1;
    return this.counterSnapshots[targetIndex] || null;
  }

  getLastCounterSnapshot(): CounterSnapshot | null {
    return this.counterSnapshots.length ? this.counterSnapshots.at(-1) : null;
  }

  /**
   * @return currently reached offset
   */
  getReachedOffset(): number {
    return this.lastOffset;
  }

  /**
   * Replay flow elements that were encountered since the given offset
   */
  replayFlowElementsFromOffset(offset: number): void {
    if (offset >= this.lastOffset) {
      return;
    }
    const context = this.context;
    const rootOffset = this.xmldoc.getElementOffset(this.root);
    if (offset < rootOffset) {
      const rootStyle = this.getStyle(this.root, false);
      if (!this.cascade.firstPageType) {
        const rootPageCV = rootStyle["page"] as CssCascade.CascadeValue;
        const rootPageType =
          rootPageCV &&
          !Css.isDefaultingValue(rootPageCV.value) &&
          rootPageCV.value.toString();
        if (rootPageType && rootPageType.toLowerCase() !== "auto") {
          this.cascade.firstPageType = rootPageType;
        }
      }
      const flowName = CssCascade.getProp(rootStyle, "flow-into");
      const flowNameStr = flowName
        ? flowName.evaluate(context, "flow-into").toString()
        : "body";
      const newFlowChunk = this.encounteredFlowElement(
        flowNameStr,
        rootStyle,
        this.root,
        rootOffset,
      );
      if (this.boxStack.empty()) {
        this.boxStack.push(rootStyle, rootOffset, true, newFlowChunk);
      }
    }
    let node = this.xmldoc.getNodeByOffset(offset);
    let nodeOffset = this.xmldoc.getNodeOffset(node, 0, false);
    if (nodeOffset >= this.lastOffset) {
      return;
    }
    while (true) {
      if (node.nodeType != 1) {
        nodeOffset += node.textContent.length;
      } else {
        const elem = node as Element;
        if (VIVLIOSTYLE_DEBUG) {
          if (nodeOffset != this.xmldoc.getElementOffset(elem)) {
            throw new Error("Inconsistent offset");
          }
        }
        const style = this.getStyle(elem, false);
        const flowName = style["flow-into"] as CssCascade.CascadeValue;
        if (flowName) {
          const flowNameStr = flowName
            .evaluate(context, "flow-into")
            .toString();
          this.encounteredFlowElement(flowNameStr, style, elem, nodeOffset);
        }
        nodeOffset++;
      }
      if (nodeOffset >= this.lastOffset) {
        break;
      }
      let next: Node | null = node.firstChild;
      if (next == null) {
        while (true) {
          next = node.nextSibling;
          if (next) {
            break;
          }
          node = node.parentNode;
          if (node === this.root) {
            return;
          }
        }
      }
      node = next;
    }
  }

  resetFlowChunkStream(flowListener: FlowListener): void {
    this.flowListener = flowListener;
    for (let i = 0; i < this.flowChunks.length; i++) {
      this.flowListener.encounteredFlowChunk(
        this.flowChunks[i],
        this.flows[this.flowChunks[i].flowName],
      );
    }
  }

  styleUntilFlowIsReached(flowName: string) {
    this.flowToReach = flowName;
    let offset = 0;
    while (true) {
      if (this.flowToReach == null) {
        break;
      }
      offset += 5000;
      if (this.styleUntil(offset, 0) == Number.POSITIVE_INFINITY) {
        break;
      }
    }
  }

  styleUntilIdIsReached(id: string) {
    if (!id) {
      return;
    }
    this.idToReach = id;
    let offset = 0;
    while (true) {
      if (!this.idToReach) {
        break;
      }
      offset += 5000;
      if (this.styleUntil(offset, 0) === Number.POSITIVE_INFINITY) {
        break;
      }
    }
    this.idToReach = null;
  }

  private encounteredFlowElement(
    flowName: string,
    style: CssCascade.ElementStyle,
    elem: Element,
    startOffset: number,
  ): Vtree.FlowChunk {
    let priority = 0;
    let linger = Number.POSITIVE_INFINITY;
    let exclusive = false;
    let repeated = false;
    let last = false;
    const optionsCV = style["flow-options"] as CssCascade.CascadeValue;
    if (optionsCV) {
      const options = CssProp.toSet(
        optionsCV.evaluate(this.context, "flow-options"),
      );
      exclusive = !!options["exclusive"];
      repeated = !!options["static"];
      last = !!options["last"];
    }
    const lingerCV = style["flow-linger"] as CssCascade.CascadeValue;
    if (lingerCV) {
      linger = CssProp.toInt(
        lingerCV.evaluate(this.context, "flow-linger"),
        Number.POSITIVE_INFINITY,
      );
    }
    const priorityCV = style["flow-priority"] as CssCascade.CascadeValue;
    if (priorityCV) {
      priority = CssProp.toInt(
        priorityCV.evaluate(this.context, "flow-priority"),
        0,
      );
    }
    const breakBefore = this.breakBeforeValues.get(startOffset) || null;
    let flow = this.flows[flowName];
    if (!flow) {
      const parentFlowName = this.boxStack.lastFlowName();
      flow = this.flows[flowName] = new Vtree.Flow(
        flowName,
        parentFlowName,
        new LayoutProcessor.BlockFormattingContext(null),
      );
    }
    const flowChunk = new Vtree.FlowChunk(
      flowName,
      elem,
      startOffset,
      priority,
      linger,
      exclusive,
      repeated,
      last,
      breakBefore,
    );
    this.flowChunks.push(flowChunk);
    if (this.flowToReach == flowName) {
      this.flowToReach = null;
    }
    this.flowListener?.encounteredFlowChunk(flowChunk, flow);
    return flowChunk;
  }

  registerForcedBreakOffset(
    breakValue: string | null,
    offset: number,
    flowName: string,
  ) {
    if (Break.isForcedBreakValue(breakValue)) {
      const forcedBreakOffsets = this.flows[flowName].forcedBreakOffsets;
      if (
        forcedBreakOffsets.length === 0 ||
        forcedBreakOffsets.at(-1) < offset
      ) {
        forcedBreakOffsets.push(offset);
      }
    }
    const previousValue = this.breakBeforeValues.get(offset);
    this.breakBeforeValues.set(
      offset,
      Break.resolveEffectiveBreakValue(previousValue, breakValue),
    );
  }

  /**
   * @param startOffset current position in the document
   * @param lookup lookup window size for the next page
   * @return lookup offset in the document for the next page
   */
  styleUntil(startOffset: number, lookup: number): number {
    let targetSlippedOffset = -1;
    let slippedOffset: number;
    if (startOffset <= this.lastOffset) {
      slippedOffset = this.offsetMap.slippedByFixed(startOffset);
      targetSlippedOffset = slippedOffset + lookup;
      if (targetSlippedOffset < this.offsetMap.getMaxSlipped()) {
        // got to the desired point
        return this.offsetMap.fixedBySlipped(targetSlippedOffset);
      }
    }
    if (this.last == null) {
      return Number.POSITIVE_INFINITY;
    }
    const context = this.context;
    while (this.last) {
      let next = this.last.firstChild;
      if (next == null) {
        while (this.last) {
          if (this.last.node.nodeType == 1) {
            this.cascade.popElement(this.last.node as Element);
            this.primary = this.primaryStack.pop();
            const box = this.boxStack.pop(this.lastOffset);
            let breakAfter: string | null = null;
            if (box.afterBox) {
              const afterPseudoBreakBefore =
                box.afterBox.getBreakValue("before");
              this.registerForcedBreakOffset(
                afterPseudoBreakBefore,
                box.afterBox.atBlockStart
                  ? this.boxStack.nearestBlockStartOffset(box)
                  : box.afterBox.offset,
                box.flowName,
              );
              breakAfter = box.afterBox.getBreakValue("after");
            }
            breakAfter = Break.resolveEffectiveBreakValue(
              breakAfter,
              box.getBreakValue("after"),
            );
            this.registerForcedBreakOffset(
              breakAfter,
              this.lastOffset,
              box.flowName,
            );
          }
          next = this.last.nextSibling;
          if (next) {
            break;
          }
          this.last = this.last.parent;
          if (!this.last) {
            if (startOffset < this.lastOffset) {
              if (targetSlippedOffset < 0) {
                slippedOffset = this.offsetMap.slippedByFixed(startOffset);
                targetSlippedOffset = slippedOffset + lookup;
              }
              if (targetSlippedOffset <= this.offsetMap.getMaxSlipped()) {
                // got to the desired point
                return this.offsetMap.fixedBySlipped(targetSlippedOffset);
              }
            }
            return Number.POSITIVE_INFINITY;
          }
        }
      }
      const cursor = next;
      this.last = cursor;
      if (cursor.node.nodeType != 1) {
        this.lastOffset += cursor.node.textContent.length;
        this.boxStack.encounteredTextNode(cursor.node);
        if (this.primary) {
          this.offsetMap.addStuckRange(this.lastOffset);
        } else {
          this.offsetMap.addSlippedRange(this.lastOffset);
        }
      } else {
        const elem = cursor.node as Base.ChildElement;
        const style = this.getAttrStyle(elem);
        this.primaryStack.push(this.primary);
        this.elementWindow = this.cascade.pushElement(
          elem,
          style,
          this.lastOffset,
        );
        if (this.cascade.lastCounterChanges.length > 0) {
          this.recordCounterSnapshot(
            this.lastOffset,
            this.cascade.counters,
            this.cascade.lastCounterChanges,
            this.cascade.lastCounterChangeTypes,
          );
        }
        const id =
          elem.getAttribute("id") || elem.getAttributeNS(Base.NS.XML, "id");
        if (id && id === this.idToReach) {
          this.idToReach = null;
        }
        if (
          !this.bodyReached &&
          elem.localName == "body" &&
          elem.parentNode == this.root
        ) {
          this.postprocessTopStyle(style, true);
          this.bodyReached = true;
        }
        let box: Box;
        const flowName = style["flow-into"] as CssCascade.CascadeValue;
        if (flowName) {
          const flowNameStr = flowName
            .evaluate(context, "flow-into")
            .toString();
          const newFlowChunk = this.encounteredFlowElement(
            flowNameStr,
            style,
            elem,
            this.lastOffset,
          );
          this.primary = !!this.primaryFlows[flowNameStr];
          box = this.boxStack.push(
            style,
            this.lastOffset,
            elem === this.root,
            newFlowChunk,
          );
        } else {
          box = this.boxStack.push(style, this.lastOffset, elem === this.root);

          // For not ignoring break-before on :root (issue #666)
          if (elem === this.xmldoc.body) {
            box.breakBefore = Break.resolveEffectiveBreakValue(
              box.flowChunk.breakBefore,
              box.breakBefore,
            );
          }
        }
        const blockStartOffset = this.boxStack.nearestBlockStartOffset(box);

        if (blockStartOffset === 0 || elem === this.xmldoc.body) {
          // Named page type at first page
          const pageCV = style["page"] as CssCascade.CascadeValue;
          const pageType =
            pageCV &&
            !Css.isDefaultingValue(pageCV.value) &&
            pageCV.value.toString();
          if (
            !this.cascade.firstPageType &&
            pageType &&
            pageType.toLowerCase() !== "auto"
          ) {
            this.cascade.firstPageType = pageType;
          }
        }

        this.registerForcedBreakOffset(
          box.breakBefore,
          blockStartOffset,
          box.flowName,
        );
        if (box.beforeBox) {
          const beforePseudoBreakAfter = box.beforeBox.getBreakValue("after");
          this.registerForcedBreakOffset(
            beforePseudoBreakAfter,
            box.beforeBox.atBlockStart ? blockStartOffset : box.offset,
            box.flowName,
          );
        }
        if (this.primary) {
          if (box.displayValue() === Css.ident.none) {
            this.primary = false;
          }
        }
        if (VIVLIOSTYLE_DEBUG) {
          const offset = this.xmldoc.getElementOffset(elem);
          if (offset != this.lastOffset) {
            throw new Error("Inconsistent offset");
          }
        }
        this.styles.setAt(this.lastOffset, style);
        this.lastOffset++;
        if (this.primary) {
          this.offsetMap.addStuckRange(this.lastOffset);
        } else {
          this.offsetMap.addSlippedRange(this.lastOffset);
        }
        if (this.bodyReached && blockStartOffset === 0) {
          // body reached but the named page type at first page is not determined
          // (Fix for issue #770)
          continue;
        }
        if (startOffset < this.lastOffset) {
          if (targetSlippedOffset < 0) {
            slippedOffset = this.offsetMap.slippedByFixed(startOffset);
            targetSlippedOffset = slippedOffset + lookup;
          }
          if (targetSlippedOffset <= this.offsetMap.getMaxSlipped()) {
            // got to the desired point
            return this.offsetMap.fixedBySlipped(targetSlippedOffset);
          }
        }
      }
    }
    return Number.POSITIVE_INFINITY;
  }

  /** @override */
  getStyle(element: Element, deep: boolean): CssCascade.ElementStyle {
    let offset = this.xmldoc.getElementOffset(element);
    if (deep) {
      offset = this.xmldoc.getNodeOffset(element, 0, true);
    }
    if (this.lastOffset <= offset) {
      this.styleUntil(offset, 0);
    }
    return this.styles.styleOf(element);
  }

  /** @override */
  processContent(
    element: Element,
    styles: { [key: string]: Css.Val },
    nodeContext: Vtree.NodeContext,
  ) {}
}

export const columnProps = ["column-count", "column-width", "column-fill"];
